import FullCalendar from "@fullcalendar/react";
import themePlugin from "@fullcalendar/react/themes/forma";
import timeGridPlugin from "@fullcalendar/react/timegrid";
import clsx from "clsx";
import dayjs, { Dayjs } from "dayjs";
import dayOfYear from "dayjs/plugin/dayOfYear";
import isBetween from "dayjs/plugin/isBetween"; // ES 2015
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import React, { useCallback, useEffect, useMemo } from "react";
import { RRule } from "rrule";
import { contiunousSequenceFromStartAndEnd, rRuleDaysToDayNums } from "../../components/Calendar/helper";
import { upcomingClosureDaysForProject } from "../../components/Helper/Calendar";
import { RepeatingCalendarEventDefinition } from "../../models/RepeatingCalendarEventDefinition";
import { CommandProjectInfo, CommandWeekDelivery } from "../CommandTypes";

import "@fullcalendar/react/skeleton.css";
import "@fullcalendar/react/themes/forma/palettes/blue.css";
import "@fullcalendar/react/themes/forma/theme.css";

dayjs.extend(dayOfYear);
dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);

interface CommandWeekCalendarProps {
    project: CommandProjectInfo;
    data: any;
}

const CommandWeekCalendar: React.FC<CommandWeekCalendarProps> = ({ project, data }) => {
    const calendarRef = React.useRef<FullCalendar>(null);
    const [closedDaysIndex, setClosedDaysIndex] = React.useState<number[]>([]);

    const deliveries = useMemo(() => data.deliveries as CommandWeekDelivery[], [data]);

    useEffect(() => {
        getUpcomingClosureDays(project);
    }, [project]);

    const getUpcomingClosureDays = async (project: any) => {
        if (!project) {
            return [];
        }

        const upcomingExceptionDays = await upcomingClosureDaysForProject(project);

        setClosedDaysIndex([...upcomingExceptionDays.closedDaysIndex]);
    };

    const openHoursDefinition = React.useMemo(() => {
        const repeatingCalendarEventDefinitions = project?.repeating_calendar_event_definitions || [];

        const def = repeatingCalendarEventDefinitions?.find(
            (def: any) => def.template_calendar_event?.name === "Project Hours"
        );

        if (def) {
            return RepeatingCalendarEventDefinition.fromApiObject(def, project.timezone);
        }

        return null;
    }, [project]);

    const deliveryHoursAllDay = React.useMemo(() => {
        if (!openHoursDefinition) {
            return true;
        }

        return (openHoursDefinition.templateCalendarEvent?.duration ?? 0) % (24 * 60) === 0;
    }, [openHoursDefinition]);

    const deliveryHoursStart = useMemo(() => {
        if (deliveryHoursAllDay || !openHoursDefinition) {
            return null;
        }

        return dayjs(openHoursDefinition.templateCalendarEvent?.startAt);
    }, [deliveryHoursAllDay, openHoursDefinition]);

    const findEarliestDeliveryHourForWeek = useCallback((): Dayjs | null => {
        if (!deliveries || deliveries.length === 0) {
            return null;
        }

        let earliestTime: number | null = null; // Time in minutes since midnight
        let earliestDateTime: Dayjs | null = null;

        deliveries.forEach((delivery) => {
            if (delivery.start_at) {
                const deliveryTime = dayjs(delivery.start_at);
                const timeInMinutes = deliveryTime.hour() * 60 + deliveryTime.minute();

                if (earliestTime === null || timeInMinutes < earliestTime) {
                    earliestTime = timeInMinutes;
                    earliestDateTime = deliveryTime;
                }
            }
        });

        return earliestDateTime;
    }, [deliveries]);

    const hiddenDays = useMemo(() => {
        if (!openHoursDefinition) {
            return [];
        }

        if (deliveryHoursAllDay) {
            const startDay = openHoursDefinition.templateCalendarEvent?.startAt?.get("day");

            if (startDay === undefined) {
                return [];
            }

            const endDay = (startDay + (openHoursDefinition.templateCalendarEvent?.duration ?? 0) / (24 * 60) - 1) % 7;

            const sequence = contiunousSequenceFromStartAndEnd(startDay, endDay).filter(
                (day) => !closedDaysIndex.includes(day)
            );

            return [0, 1, 2, 3, 4, 5, 6].filter((day) => !sequence.includes(day));
        }

        const rrule = RRule.fromString(openHoursDefinition?.rrule);

        const closedDays = rRuleDaysToDayNums(rrule.options.byweekday as number[]).filter(
            (day) => !closedDaysIndex.includes(day)
        );
        return [0, 1, 2, 3, 4, 5, 6].filter((day) => !closedDays.includes(day));
    }, [deliveryHoursAllDay, openHoursDefinition, closedDaysIndex]);

    const scrollTime = useMemo(() => {
        let start = dayjs().set("hour", 6).set("minute", 30);

        if (deliveryHoursAllDay) {
            start = dayjs().startOf("day");
        }

        if (deliveryHoursStart && !deliveryHoursAllDay) {
            start = dayjs(deliveryHoursStart);
        }

        const earliestDelivery = findEarliestDeliveryHourForWeek();

        if (earliestDelivery && earliestDelivery.isBefore(start)) {
            start = earliestDelivery;
        }

        return start.subtract(2, "hours").format("HH:mm:ss");
    }, [deliveryHoursStart, deliveryHoursAllDay, findEarliestDeliveryHourForWeek]);

    useEffect(() => {
        if (calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            calendarApi.scrollToTime(scrollTime);
        }
    }, [scrollTime, calendarRef]);

    const events = useMemo(() => {
        return deliveries.map((calendarEvent) => {
            const color = calendarEvent.color_by || "#FF9E00";
            const isPending = calendarEvent.status === "requested";
            const isDeclined = calendarEvent.status === "declined";

            let classNames = "";
            if (isDeclined) {
                classNames = clsx("fc-event-edge-only", "fc-event-edge-declined");
            } else if (isPending) {
                classNames = clsx("fc-event-edge-only", "fc-event-edge-striped");
            } else {
                classNames = clsx("fc-event-edge-only");
            }

            let title = "";

            title = calendarEvent.name;

            const initials = calendarEvent.receiving_team?.initials;

            if (initials) {
                title = `[${initials}] ${title}`;
            }

            return {
                groupId: initials || "",
                id: calendarEvent.id.toString(),
                className: classNames,
                borderColor: color,
                backgroundColor: `${color.slice(0, 7).padEnd(7, "0")}33`,
                color: `${color.slice(0, 7).padEnd(7, "0")}33`,
                title: title,
                start: new Date(calendarEvent.start_at),
                end: new Date(calendarEvent.end_at),
                object: calendarEvent,
                display: "block",
                baseColor: color, // Store for use in eventDidMount
            };
        });
    }, [deliveries]);

    return (
        <div id="weekCalendar" className="h-full grow">
            <FullCalendar
                ref={calendarRef}
                height="100%"
                plugins={[themePlugin, timeGridPlugin]}
                timeZone={project.timezone}
                initialView="timeGridWeek"
                rerenderDelay={100}
                events={events}
                slotEventOverlap={false}
                eventContent={(arg) => {
                    const title = arg.event.title;
                    const duration =
                        arg.event.start && arg.event.end
                            ? dayjs(arg.event.end).diff(dayjs(arg.event.start), "minute")
                            : 0;

                    return (
                        <div className="fc-event-main-frame">
                            {title && <div className="fc-event-title fc-sticky">{title}</div>}
                            {duration > 30 && arg.timeText && <div className="fc-event-time">{arg.timeText}</div>}
                        </div>
                    );
                }}
                eventDidMount={(info) => {
                    if (info.event.display === "background" || info.event.display === "inverse-background") return;

                    const baseColor = info.event.extendedProps?.baseColor as string | undefined;
                    if (!baseColor) return;

                    // FullCalendar v7 has removed ability to set different background and border colors, so we have to specify manually here.
                    const bgColor = `${baseColor.slice(0, 7).padEnd(7, "0")}33`;

                    // Direct fallback in case custom classes override FC vars
                    info.el.style.setProperty("border-color", baseColor, "important");
                    info.el.style.setProperty("background-color", bgColor, "important");
                }}
                headerToolbar={false}
                allDaySlot={false}
                selectable={false}
                weekends={true}
                hiddenDays={hiddenDays}
                slotLaneClass="fc-override-lane"
                slotHeaderInnerClass="fc-override-header-inner"
                slotHeaderClass="fc-override-header"
            />
        </div>
    );
};

export default CommandWeekCalendar;
